import type {
  InfiniteData,
  UseInfiniteQueryResult,
} from '@tanstack/react-query'
import { useInfiniteQuery } from '@tanstack/react-query'

import { fetchMultisigGroups, MultisigGroupsResponse } from '@/api'
import { buildBasePagination } from '@/helpers/pagination'
import { queryKeys } from '@/queries/keys'
import { PaginationParams } from '@/types'

interface QueryOptions {
  enabled?: boolean
  staleTime?: number
  refetchInterval?: number
}

interface UseMultisigGroupsInfiniteListOptions {
  pagination?: PaginationParams
  queryOptions?: QueryOptions
}

export function useMultisigGroupsInfiniteList(
  options?: UseMultisigGroupsInfiniteListOptions,
) {
  const basePagination = buildBasePagination(options?.pagination)
  type MultisigGroupsInfiniteData = InfiniteData<
    MultisigGroupsResponse,
    string | undefined
  >

  return useInfiniteQuery<
    MultisigGroupsResponse,
    unknown,
    MultisigGroupsInfiniteData,
    ReturnType<typeof queryKeys.multisig.groupsList>,
    string | undefined
  >({
    queryKey: queryKeys.multisig.groupsList(basePagination),
    initialPageParam: options?.pagination?.key,
    queryFn: ({ pageParam }) =>
      fetchMultisigGroups({
        ...basePagination,
        key: pageParam,
      }),
    getNextPageParam: lastPage => lastPage.pagination.next_key ?? undefined,
    enabled: options?.queryOptions?.enabled ?? true,
    staleTime: options?.queryOptions?.staleTime,
    refetchInterval: options?.queryOptions?.refetchInterval,
  }) as UseInfiniteQueryResult<MultisigGroupsInfiniteData, unknown>
}
